/* Home dashboard (Start Page). Greeting, action items,
 * a customizable widget grid (drag to reorder, resize 1<->2 cols, add/remove, persisted to
 * localStorage), and the right info rail. Wired to real data where a hook exists; the rest
 * use typed placeholder shapes marked with TODO(home) until their sources are available. */
import { DragEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowCounterclockwise,
  ArrowsAngleContract,
  ArrowsAngleExpand,
  CalendarEvent,
  CalendarWeek,
  CaretDownFill,
  Check2Circle,
  ChevronDown,
  ChevronRight,
  ExclamationCircle,
  FileEarmarkText,
  Grid1x2,
  Grid3x3Gap,
  GripVertical,
  InfoCircle,
  People,
  PersonBadge,
  PersonFill,
  PlusLg,
  XLg,
} from "react-bootstrap-icons";
import { Link, useNavigate } from "react-router";
import { Dropdown } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import QueryKeys from "../../api/queryKeys";
import { useFSMeetingLocations } from "../../api/fsmeetings";
import { useInfoBoard } from "../../api/infoboard";
import { useEvents } from "../../api/meetings";
import { midweekApi } from "../../api/midweek";
import { useOtherSchedule } from "../../api/otherSchedule";
import useReportTotals from "../../api/reportTotals";
import { summaryApi } from "../../api/summary";
import { useUsers } from "../../api/user";
import { useWMSchedules } from "../../api/weekend";
import useFSGroups from "../../api/fsgroup";
import { canGetSummaryTotals } from "../../helpers/permission";
import { HGContext } from "../../helpers/globals";
import {
  getDayjs,
  localizedDateRange,
  localizedMonthYear,
  localizedTime,
  Month,
  mmDate,
  stringToDate,
  weekRange,
  wmDate,
} from "../../helpers/dateHelpers";
import { OtherScheduleKind } from "../../types/scheduling/other";
import { userCompare, userIdMap } from "../../helpers/user";
import User from "../../types/user";
import { getGroupDisplayName } from "../../helpers/fsgroup";
import { EventTypes, Events } from "../../types/scheduling/events";
import { MidweekMeetingPartAsset } from "../../types/scheduling/midweek";
import { Permission } from "../../types/permission";
import {
  ActionItem,
  ASSIGNMENT_LINKS,
  DEFAULT_LAYOUT,
  isKnownWidget,
  LayoutItem,
  LAYOUT_LS_KEY,
  WHATSNEW_COLLAPSED_LS_KEY,
  WHATSNEW_RAIL_MAX,
  WIDGET_CATALOG,
  WidgetDef,
  WidgetId,
  widgetById,
} from "./homeData";
import { congId, eventLabel, noMidweekMeetingEvents, useShortDate } from "./homeShared";
import { localizedText, useAnnouncements } from "../whatsnew/announcements";
import { noMeetingEvents } from "../scheduling/wm/wm_common";
import {
  AVWidget,
  CleaningWidget,
  CustomAssignmentsWidget,
  MidweekUpcomingWidget,
  TerritoryDueWidget,
  WeekendUpcomingWidget,
} from "./scheduleWidgets";
import { tPositional } from "../../helpers/i18n";
import { useCongSettings } from "../../api/cong";

export default function HomeDashboard() {
  return (
    <div className="hp-page">
      <div className="hp">
        <div className="hp-main">
          <Greeting />
          <ActionItems />
          <WidgetGrid />
        </div>
        <Rail />
      </div>
    </div>
  );
}

/* ===================== Greeting ===================== */
function Greeting() {
  const { t } = useTranslation();
  const ctx = useContext(HGContext);
  const user = ctx.globals.authUser;
  const name = user?.firstname || user?.displayName || "";
  const h = new Date().getHours();
  const greeting =
    h < 12 ? t("home.greeting.morning") : h < 18 ? t("home.greeting.afternoon") : t("home.greeting.evening");
  return (
    <div className="hp-greeting">
      <span className="hp-greeting__avatar">
        <PersonFill size={16} />
      </span>
      {greeting}
      {name ? (
        <>
          , <b>{name}</b>
        </>
      ) : null}
    </div>
  );
}

/* ===================== Action items ===================== */
function useActionItems(): ActionItem[] {
  const { t } = useTranslation();
  const ctx = useContext(HGContext);
  const items: ActionItem[] = [];

  // after the 20th, publishers' reports and the congregation's monthly report to the branch are
  // both considered overdue rather than merely outstanding.
  const isOverdue = new Date().getDate() > 20;

  // Missing publisher reports for the working month.
  const totals = useReportTotals({ enabled: canGetSummaryTotals() }).data;
  const missing = totals?.missingUserIds.length ?? 0;
  if (missing > 0) {
    items.push({
      icon: ExclamationCircle,
      label: t("report.title.missing"),
      count: missing,
      tag: "",
      tone: "warn",
      to: "/missing",
    });
  }

  // Congregation monthly report not yet submitted to the branch (submittedAt unset).
  const canSubmit = ctx.globals.permissions.has(Permission.UpdateReports);
  const month = ctx.globals.workingMonth;
  const summary = useQuery({
    queryKey: [QueryKeys.SummaryMonth, month],
    queryFn: () => summaryApi.getMonthSummary(month),
    enabled: canSubmit,
  }).data;
  if (canSubmit && summary && !summary.submittedAt) {
    items.push({
      icon: Check2Circle,
      label: t("home.action.submit-report"),
      tag: isOverdue ? t("home.action.overdue") : t("general.not-submitted"),
      tone: isOverdue ? "danger" : "warn",
      to: "/monthSummary",
    });
  }

  return items;
}

function ActionItems() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items = useActionItems();
  return (
    <section className="hp-actions">
      <h2 className="hp-h2">
        <Check2Circle size={15} /> {t("home.actions.title")}
      </h2>
      <div className="hp-actionlist">
        {items.map((a, i) => {
          const Ico = a.icon;
          return (
            <div className="hp-action" key={i} role="link" tabIndex={0} onClick={() => navigate(a.to)}>
              <Ico className={"hp-action__icon hp-action__icon--" + a.tone} size={17} />
              <span className="hp-action__label">
                {a.label}
                {typeof a.count === "number" && <span className="hp-action__count">{a.count}</span>}
              </span>
              <span className="hp-action__right">
                {a.meta && <span className="hp-action__meta">{a.meta}</span>}
                {!!a.tag && <span className={"hp-tag hp-tag--" + a.tone}>{a.tag}</span>}
                <ChevronRight className="hp-action__chev" size={12} />
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ===================== Widget grid ===================== */
function loadLayout(): LayoutItem[] {
  try {
    const raw = JSON.parse(localStorage.getItem(LAYOUT_LS_KEY) || "null");
    if (raw && Array.isArray(raw.items)) {
      // Drop any persisted ids no longer in the catalogue (e.g. the removed "next-appointments" tile).
      const items = (raw.items as LayoutItem[]).filter((it) => isKnownWidget(it.id));
      if (items.length > 0) return items;
    }
  } catch {
    /* corrupt / unavailable */
  }
  return DEFAULT_LAYOUT.map((id) => ({ id, span: widgetById(id).span }));
}

function WidgetGrid() {
  const { t } = useTranslation();
  const [items, setItems] = useState<LayoutItem[]>(loadLayout);
  const [addOpen, setAddOpen] = useState(false);
  const dragId = useRef<WidgetId | null>(null);
  const [overId, setOverId] = useState<WidgetId | null>(null);
  const addRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(LAYOUT_LS_KEY, JSON.stringify({ items }));
  }, [items]);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // ignore the Add buttons (header + empty-state) so their own onClick can toggle the menu
      if (addRef.current && !addRef.current.contains(target) && !target.closest(".hp-addbtn")) setAddOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const available: WidgetDef[] = WIDGET_CATALOG.filter((w) => !items.some((it) => it.id === w.id));

  const reorder = (fromId: WidgetId, toId: WidgetId) => {
    if (fromId === toId) return;
    setItems((prev) => {
      const arr = [...prev];
      const from = arr.findIndex((x) => x.id === fromId);
      const to = arr.findIndex((x) => x.id === toId);
      if (from < 0 || to < 0) return prev;
      const [moved] = arr.splice(from, 1);
      if (!moved) return prev;
      arr.splice(to, 0, moved);
      return arr;
    });
  };

  return (
    <section className="hp-grid-wrap">
      <div className="hp-grid-head">
        <h2 className="hp-h2">
          <Grid1x2 size={15} /> {t("general.overview")}
        </h2>
        <div className="hp-grid-tools" ref={addRef}>
          <button className="hp-addbtn" onClick={() => setAddOpen((o) => !o)}>
            <PlusLg size={14} /> {t("home.overview.add")}
          </button>
          {addOpen && (
            <div className="hp-addmenu">
              <div className="hp-addmenu__eyebrow">{t("home.overview.add")}</div>
              {available.length === 0 && <div className="hp-addmenu__empty">{t("home.overview.all-added")}</div>}
              {available.map((w) => {
                const Ico = w.icon;
                return (
                  <button
                    key={w.id}
                    className="hp-addmenu__item"
                    onClick={() => {
                      setItems((prev) => [...prev, { id: w.id, span: w.span }]);
                      setAddOpen(false);
                    }}
                  >
                    <Ico size={15} /> {t(w.titleKey)}
                  </button>
                );
              })}
              <div className="hp-addmenu__sep" />
              <button
                className="hp-addmenu__reset"
                onClick={() => {
                  setItems(DEFAULT_LAYOUT.map((id) => ({ id, span: widgetById(id).span })));
                  setAddOpen(false);
                }}
              >
                <ArrowCounterclockwise size={15} /> {t("home.overview.reset")}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="hp-grid">
        {items.map((it) => {
          const def = widgetById(it.id);
          const isDrop = overId === it.id && dragId.current !== null && dragId.current !== it.id;
          return (
            <div
              key={it.id}
              className={"hp-widget hp-widget--span" + it.span + (isDrop ? " is-drop-target" : "")}
              onDragOver={(e: DragEvent) => {
                e.preventDefault();
                setOverId(it.id);
              }}
              onDrop={(e: DragEvent) => {
                e.preventDefault();
                if (dragId.current) reorder(dragId.current, it.id);
                dragId.current = null;
                setOverId(null);
              }}
            >
              <div
                className="hp-widget__head"
                draggable
                onDragStart={(e: DragEvent) => {
                  dragId.current = it.id;
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragEnd={() => {
                  dragId.current = null;
                  setOverId(null);
                }}
              >
                <GripVertical className="hp-widget__grip" size={15} />
                <def.icon className="hp-widget__icon" size={15} />
                <span className="hp-widget__title">{t(def.titleKey)}</span>
                <div className="hp-widget__ctrls">
                  <button
                    className="hp-wbtn hp-wbtn--resize"
                    title={t("general.expand-collapse-view")}
                    onClick={() =>
                      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, span: x.span === 2 ? 1 : 2 } : x)))
                    }
                  >
                    {it.span === 2 ? <ArrowsAngleContract size={13} /> : <ArrowsAngleExpand size={13} />}
                  </button>
                  <button
                    className="hp-wbtn hp-wbtn--danger"
                    title={t("general.remove")}
                    onClick={() => setItems((prev) => prev.filter((x) => x.id !== it.id))}
                  >
                    <XLg size={13} />
                  </button>
                </div>
              </div>
              <div className="hp-widget__body">
                <WidgetBody id={it.id} />
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="hp-grid-empty">
            <Grid3x3Gap size={26} />
            <p>{t("home.overview.empty")}</p>
            <button className="hp-addbtn" onClick={() => setAddOpen(true)}>
              <PlusLg size={14} /> {t("home.overview.add")}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ===================== Widget bodies ===================== */
function WidgetBody({ id }: { id: WidgetId }) {
  switch (id) {
    case "reports":
      return <ReportsWidget />;
    case "assignments":
      return <AssignmentsWidget />;
    case "week-meetings":
      return <WeekMeetingsWidget />;
    case "mm-upcoming":
      return <MidweekUpcomingWidget />;
    case "wm-upcoming":
      return <WeekendUpcomingWidget />;
    case "av":
      return <AVWidget />;
    case "cleaning":
      return <CleaningWidget />;
    case "custom":
      return <CustomAssignmentsWidget />;
    case "territory-due":
      return <TerritoryDueWidget />;
    case "field-service":
      return <FieldServiceWidget />;
    case "groups":
      return <GroupsWidget />;
    // case "recent-activity":
    //   return <ActivityWidget />;
  }
}

type ReportCatKey = "self" | "admin" | "missing";

function ReportsWidget() {
  const { t, i18n } = useTranslation();
  const totals = useReportTotals({ enabled: canGetSummaryTotals() }).data;
  const usersQuery = useUsers();
  const userMap = useMemo(() => userIdMap(usersQuery.data ?? []), [usersQuery.data]);
  const [openCat, setOpenCat] = useState<ReportCatKey | null>(null);

  const selfIds = totals?.enteredBySelfUserIds ?? [];
  const adminIds = totals?.enteredByAdminUserIds ?? [];
  const missingIds = totals?.missingUserIds ?? [];
  const reported = selfIds.length + adminIds.length;
  const total = totals?.userCount ?? 0;
  const pct = total > 0 ? Math.round((reported / total) * 100) : 0;
  const month = totals?.month ? localizedMonthYear(totals.month, i18n.language) : "";

  // Donut segment boundaries (cumulative %): self, then self+admin, missing fills the rest.
  const selfPct = total > 0 ? (selfIds.length / total) * 100 : 0;
  const reportedPct = total > 0 ? (reported / total) * 100 : 0;

  // Break reports out by who entered them — the publisher themselves, an admin on their
  // behalf, or not yet reported — matching the previous home-screen chart.
  const cats: { key: ReportCatKey; ids: number[]; label: string; color: string }[] = [
    { key: "self", ids: selfIds, label: t("chart.legend.publisher"), color: "var(--hp-success)" },
    { key: "admin", ids: adminIds, label: t("chart.legend.admin"), color: "var(--hp-gold)" },
    { key: "missing", ids: missingIds, label: t("chart.legend.missing"), color: "var(--hp-danger)" },
  ];

  const namesOf = (ids: number[]) =>
    ids
      .map((id) => userMap.get(id))
      .filter((u): u is User => !!u)
      .sort(userCompare)
      .map((u) => u.displayName);

  return (
    <div className="w-reports">
      <div className="w-reports__head">
        <div
          className={"w-donut" + (total === 0 ? " is-empty" : "")}
          style={{ ["--self" as string]: selfPct, ["--adm" as string]: reportedPct }}
        >
          <span>{pct}%</span>
        </div>
        <div>
          <div className="w-reports__big">
            {reported} <span>/ {total}</span>
          </div>
          <div className="w-reports__sub">
            {t("reportlist.reported")}
            {month ? " · " + month : ""}
          </div>
        </div>
      </div>

      {/* click a category to see who's in it (publisher-entered, admin-entered, or still missing) */}
      <div className="w-reports__cats">
        {cats.map((c) =>
          c.ids.length > 0 ? (
            <Dropdown
              key={c.key}
              className="dropdown-bounded"
              show={openCat === c.key}
              onToggle={(isOpen) => setOpenCat(isOpen ? c.key : null)}
            >
              <Dropdown.Toggle as="button" type="button" bsPrefix="w-reports__cat">
                <span className="w-reports__dot" style={{ background: c.color }} />
                <span className="w-reports__catlabel">{c.label}</span>
                <span className="w-reports__catnum">{c.ids.length}</span>
                <CaretDownFill size={9} className="w-reports__caret" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {c.key === "missing" && (
                  <>
                    <Dropdown.Item as={Link} to="/missing">
                      {t("report.title.missing")} <ChevronRight size={10} />
                    </Dropdown.Item>
                    <Dropdown.Divider />
                  </>
                )}
                {namesOf(c.ids).map((n) => (
                  <Dropdown.ItemText key={n}>{n}</Dropdown.ItemText>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          ) : null,
        )}
      </div>
    </div>
  );
}

function AssignmentsWidget() {
  const { t } = useTranslation();
  return (
    <div className="w-linkgrid">
      {ASSIGNMENT_LINKS.map((l) => {
        const Ico = l.icon;
        return (
          <Link className="w-link" to={l.to} key={l.to + l.key}>
            <Ico size={14} />
            <span>{t(l.key, l.label)}</span>
          </Link>
        );
      })}
    </div>
  );
}

type MeetingBlock = {
  day: string;
  chair: string;
  parts: { part: string; who: string }[];
  // set when an event (memorial/assembly/convention) cancels this meeting entirely.
  cancelledLabel?: string;
};

function MeetingCol({ title, block }: { title: string; block: MeetingBlock }) {
  const { t } = useTranslation();
  return (
    <div className="w-mtg">
      <div className="w-mtg__head">
        <span className="w-mtg__title">{title}</span>
        <span className="w-mtg__day">{block.day}</span>
      </div>
      {block.cancelledLabel ? (
        <div className="w-empty">{block.cancelledLabel}</div>
      ) : (
        <>
          <div className="w-mtg__chair">
            <PersonBadge size={13} /> {t("schedules.chairman")} · {block.chair}
          </div>
          <ul className="w-mtg__list">
            {block.parts.map((p, i) => (
              <li key={i}>
                <span className="w-mtg__part">{p.part}</span>
                <span className="w-mtg__who">{p.who}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function WeekMeetingsWidget() {
  const { t, i18n } = useTranslation();
  const ctx = useContext(HGContext);
  const shortDate = useShortDate();
  const cid = congId(ctx);
  const { from, to } = weekRange();
  const userMap = userIdMap(useUsers().data ?? []);
  const nameOf = (id?: number | null) => (id ? (userMap.get(id)?.displayName ?? "—") : "—");
  const congSettings = useCongSettings();

  const mm = useQuery({
    queryKey: [QueryKeys.MidweekInfo, cid, from, to],
    queryFn: () => midweekApi.getMeetingsInfo(from, to, cid, false),
    enabled: !!cid,
  }).data;
  const wm = useWMSchedules(from, to, cid, { enabled: !!cid }).data ?? [];

  // Midweek: pair each scheduled assignment with the title of its part.
  const meeting = mm?.meetings?.[0];
  const schedule = mm?.schedules?.[0];
  // tgw/fm/lac can be omitted entirely by the backend (e.g. no student parts scheduled yet)
  // despite the type declaring them as required arrays, so guard each before spreading.
  const partsById = new Map(
    (meeting ? [...(meeting.tgw ?? []), ...(meeting.fm ?? []), ...(meeting.lac ?? [])] : []).map((p) => [p.id, p]),
  );
  const midweekParts: { part: string; who: string }[] = [];
  if (schedule) {
    [...(schedule.tgw ?? []), ...(schedule.fm ?? []), ...(schedule.lac ?? [])].forEach((sa) => {
      const p = partsById.get(sa.part);
      if (p) midweekParts.push({ part: t(MidweekMeetingPartAsset[p.type]), who: nameOf(sa.assignee) });
    });
  }
  const midweekCancelled = !!schedule?.event && noMidweekMeetingEvents.has(schedule.event.event);
  const midweek: MeetingBlock | null =
    (meeting || schedule) && ctx.globals.cong
      ? {
          day: meeting
            ? shortDate(mmDate(meeting.date, ctx.globals.cong, schedule?.event ? [schedule.event] : undefined))
            : "",
          chair: nameOf(schedule?.chairman),
          parts: midweekParts,
          cancelledLabel: midweekCancelled ? eventLabel(t, schedule!.event!) : undefined,
        }
      : null;

  // Weekend.
  const w = wm[0];
  const weekendCancelled = !!w?.event && noMeetingEvents.has(w.event.event);
  const speaker = w
    ? [w.speaker?.firstname, w.speaker?.lastname].filter(Boolean).join(" ") || nameOf(w.speaker?.userId)
    : "";
  const weekend: MeetingBlock | null =
    w && ctx.globals.cong
      ? {
          day: shortDate(wmDate(w.date, ctx.globals.cong, w.event ? [w.event] : undefined)),
          chair: nameOf(w.wm_chairman),
          parts: [
            // schedules.assignment.public-talk is "Public Talk {{0}}" — no talk number here, so blank it and trim.
            {
              part: w.public_talk?.title || tPositional(t, "schedules.assignment.public-talk", "").trim(),
              who: speaker,
            },
            { part: t("conganalysis.attendance.wt"), who: nameOf(w.wt_conductor ?? congSettings.data?.wt_conductor) },
            { part: t("schedules.weekend.wt-reader"), who: nameOf(w.wm_reader) },
          ],
          cancelledLabel: weekendCancelled ? eventLabel(t, w!.event!) : undefined,
        }
      : null;

  if (!midweek && !weekend) {
    return <div className="w-empty">{t("home.week.none")}</div>;
  }
  return (
    <div className="w-week">
      <div className="w-week__range">
        <CalendarWeek size={13} /> {localizedDateRange(stringToDate(from), stringToDate(to), i18n.language)}
      </div>
      <div className="w-week__cols">
        {midweek && <MeetingCol title={t("conganalysis.attendance.midweek")} block={midweek} />}
        {weekend && <MeetingCol title={t("conganalysis.attendance.weekend")} block={weekend} />}
      </div>
    </div>
  );
}

function FieldServiceWidget() {
  const { t, i18n } = useTranslation();
  const shortDate = useShortDate();
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const todayDow = getDayjs(now).day(); // 0 = Sunday .. 6 = Saturday, matching dayjs .day() used to expand weekly entries
  const month = Month.fromDate(now);
  const locations = useFSMeetingLocations().data ?? [];
  // read the actual schedule (kind FieldMinistry) rather than the recurring templates — templates
  // are just defaults for auto-fill/availability and can diverge from what's really scheduled.
  // "weekly" entries recur every week on their anchor date's day-of-week, same as the fsmeetings
  // schedule page (schedule.tsx getEvents()); others are matched by exact date.
  const schedules = useOtherSchedule(OtherScheduleKind.FieldMinistry, month.start(), month.end()).data ?? [];
  const locName = new Map(locations.map((l) => [l.id, l.name]));
  const todays = schedules
    .filter((os) => (os.weekly ? getDayjs(os.date, true).day() === todayDow : os.date === today))
    .sort((a, b) => (a.time ?? "").localeCompare(b.time ?? ""));
  return (
    <div className="w-fs">
      <div className="w-fs__date">
        <CalendarEvent size={13} /> {shortDate(today, true)}
      </div>
      {todays.length === 0 ? (
        <div className="w-empty">{t("home.fs.none")}</div>
      ) : (
        <ul className="w-fs__list">
          {todays.map((os) => (
            <li key={os.id}>
              <span className="w-fs__time">{os.time ? localizedTime(os.time, i18n.language, "") : ""}</span>
              <div className="w-fs__body">
                <span className="w-fs__place">
                  {(os.fsMeetingLocationId && locName.get(os.fsMeetingLocationId)) ??
                    t("nav.schedules.meetings.field-service")}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GroupsWidget() {
  const { t } = useTranslation();
  const ctx = useContext(HGContext);
  const groups = useFSGroups({ enabled: !!congId(ctx) }).data ?? [];
  const usersQuery = useUsers();
  const totals = useReportTotals({ enabled: canGetSummaryTotals() }).data;
  const usersById = userIdMap(usersQuery.data ?? []);
  // reported = users in the group who have a report this month (self- or admin-entered);
  // total = users in the group who are part of the reporting set (reported or missing).
  const reportedSet = new Set([...(totals?.enteredBySelfUserIds ?? []), ...(totals?.enteredByAdminUserIds ?? [])]);
  const reportingSet = new Set([...reportedSet, ...(totals?.missingUserIds ?? [])]);
  if (groups.length === 0) return <div className="w-empty">{t("home.groups.none")}</div>;
  return (
    <div className="w-groups">
      {groups.slice(0, 6).map((g) => {
        const overseer = usersById.get(g.overseer_id)?.displayName ?? "";
        // getGroupDisplayName falls back to the overseer's name when the group has no name of its own.
        const name = getGroupDisplayName(g, usersById);
        const members = (usersQuery.data ?? []).filter((u) => u.group_id === g.id);
        const total = members.filter((u) => reportingSet.has(u.id)).length;
        const reported = members.filter((u) => reportedSet.has(u.id)).length;
        return (
          <div className="w-group" key={g.id}>
            <div className="w-group__main">
              <span className="w-group__name">{name}</span>
              {g.name && overseer && <span className="w-group__ov">{overseer}</span>}
            </div>
            <div className="w-group__meta">
              <span className="w-group__pub">
                <People size={13} /> {members.length}
              </span>
              {total > 0 && (
                <span className={"w-group__rep" + (reported >= total ? "" : " is-partial")}>
                  {reported}/{total} {t("reportlist.reported")}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ===================== Right rail ===================== */
function Rail() {
  const { t } = useTranslation();
  return (
    <aside className="hp-rail">
      <WhatsNewSection />
      <NewDocumentsSection />
      <UpcomingEventsSection />
      <span className="visually-hidden">{t("smpw.information")}</span>
    </aside>
  );
}

// const ACTIVITY_SECTION_LABELS: Record<string, string> = {
//   mm: "Midweek meeting",
//   wm: "Weekend meeting",
//   pub: "Publishers",
//   fsg: "Field service groups",
//   ter: "Territories",
//   ava: "Sound and stage",
//   abs: "Absences",
//   pw: "Public witnessing",
//   cong: "Congregation",
//   att: "Attendance",
//   per: "Person",
//   fam: "Family",
//   infoboard: "Information board",
//   custom_assignment: "Custom assignments",
//   clean: "Cleaning",
//   maint: "Maintenance",
//   not: "Notifications",
//   fsm: "Field service",
//   tag: "Tags",
//   mtg: "Meetings",
// };

/** Right-rail "What's new" — the recent in-app feature announcements. Integrated into the home
 * widget (razor-1 review on #2597) instead of a separate nav entry; "View more" opens the full
 * /whatsnew changelog. Titles are self-contained localized data (localizedText). */
function loadWhatsNewCollapsed(): boolean {
  try {
    return localStorage.getItem(WHATSNEW_COLLAPSED_LS_KEY) === "1";
  } catch {
    /* unavailable / blocked — just start expanded */
    return false;
  }
}

function WhatsNewSection() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const shortDate = useShortDate();
  const items = useAnnouncements();
  const recent = items.slice(0, WHATSNEW_RAIL_MAX);
  const [collapsed, setCollapsed] = useState(loadWhatsNewCollapsed);

  // persist in an effect, not inside the updater: React may call a state updater more than once,
  // so it has to stay pure (this mirrors how the widget layout is persisted)
  useEffect(() => {
    try {
      localStorage.setItem(WHATSNEW_COLLAPSED_LS_KEY, collapsed ? "1" : "0");
    } catch {
      /* unavailable — the state just won't survive a reload */
    }
  }, [collapsed]);

  return (
    <section className="hp-railsec">
      <h3 className="hp-railh">
        <InfoCircle className="hp-railinfo" size={14} />
        <button
          type="button"
          className="hp-railtoggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls="hp-whatsnew-body"
        >
          {t("home.rail.whatsnew")}
          {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
        </button>
      </h3>
      {!collapsed && (
        <div id="hp-whatsnew-body">
          {recent.length === 0 ? (
            <div className="hp-railempty">{t("general.none")}</div>
          ) : (
            <ul className="hp-news">
              {recent.map((a) => (
                <li key={a.id}>
                  <span className="hp-news__tag">{a.area}</span>
                  <span className="hp-news__text">{localizedText(a.title, i18n.language)}</span>
                  <span className="hp-news__when">{shortDate(a.date)}</span>
                </li>
              ))}
            </ul>
          )}
          {/* always offered as the way into the full What's New page, even when the rail already
              shows every announcement */}
          {items.length > 0 && (
            <a
              className="hp-railmore"
              onClick={() => navigate("/whatsnew")}
              role="link"
              tabIndex={0}
              style={{ cursor: "pointer" }}
            >
              {t("general.more")}
            </a>
          )}
        </div>
      )}
    </section>
  );
}

/** Recent-activity widget — the congregation audit log, moved out of the rail's "What's new"
 * into its own dashboard widget. The activity endpoint is admin-scoped, so non-admins just
 * see the empty state. */
// function ActivityWidget() {
//   const { t } = useTranslation();
//   const usersById = userIdMap(useUsers().data ?? []);
//   const now = Date.now();
//   const from = new Date(now - 14 * 86400000).toISOString().slice(0, 10);
//   const to = new Date(now).toISOString().slice(0, 10);
//   const logs =
//     useQuery({ queryKey: [QueryKeys.Activity, from, to], queryFn: () => meetingsApi.getActivityLogs(from, to) }).data ??
//     [];
//
//   const relative = (ms: number): string => {
//     const mins = Math.floor(Math.max(0, now - ms) / 60000);
//     if (mins < 60) return t("home.time.minutes", { count: mins });
//     const hours = Math.floor(mins / 60);
//     if (hours < 24) return t("home.time.hours", { count: hours });
//     const days = Math.floor(hours / 24);
//     return days === 1 ? t("home.time.yesterday") : t("home.time.days", { count: days });
//   };
//
//   const events = [...logs].sort((a, b) => b["@timestamp"] - a["@timestamp"]).slice(0, 5);
//   if (events.length === 0) {
//     return <div className="w-empty">{t("home.rail.whatsnew-empty")}</div>;
//   }
//   return (
//     <ul className="hp-news">
//       {events.map((e, i) => {
//         const who = usersById.get(e.user_id)?.displayName ?? "";
//         return (
//           <li key={i}>
//             <span className="hp-news__tag">
//               {t("home.activity.section." + e.section, ACTIVITY_SECTION_LABELS[e.section] ?? e.section)}
//             </span>
//             <span className="hp-news__text">
//               {t("home.activity.action." + e.action, e.action)}
//               {who ? " · " + who : ""}
//             </span>
//             <span className="hp-news__when">{relative(e["@timestamp"])}</span>
//           </li>
//         );
//       })}
//     </ul>
//   );
// }

function NewDocumentsSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ctx = useContext(HGContext);
  const shortDate = useShortDate();
  const info = useInfoBoard(congId(ctx), { enabled: !!congId(ctx) }).data;
  const catName = (id: number | null) =>
    info?.categories.find((c) => c.id === id)?.name ?? t("general.information-board");
  // only surface genuinely new documents — those from the last week (razor-1 review on #2572).
  // TODO: once a lastLogin timestamp is exposed on Whoami/User, prefer "since your last login".
  const RECENT_DOCS_DAYS = 7;
  const recentCutoff = Date.now() - RECENT_DOCS_DAYS * 24 * 60 * 60 * 1000;
  const allDocs = info?.itemLinks ?? [];
  const docs = allDocs
    .filter((d) => new Date(d.createdAt).getTime() >= recentCutoff)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
    .slice(0, 4);
  return (
    <section className="hp-railsec">
      <h3 className="hp-railh">
        <FileEarmarkText size={14} /> {t("home.rail.documents")}
      </h3>
      {docs.length === 0 && <div className="hp-railempty">{t("general.none")}</div>}
      <ul className="hp-docs">
        {docs.map((d) => (
          <li key={d.id}>
            <a className="hp-doc" href={d.url} target="_blank" rel="noopener noreferrer">
              <span className="hp-doc__title">{d.title}</span>
            </a>
            <span className="hp-doc__meta">
              {catName(d.categoryId)} · {shortDate(d.createdAt)}
            </span>
          </li>
        ))}
      </ul>
      {allDocs.length > 0 && (
        <a
          className="hp-railmore"
          onClick={() => navigate("/infoboard")}
          role="link"
          tabIndex={0}
          style={{ cursor: "pointer" }}
        >
          {t("general.more")}
        </a>
      )}
    </section>
  );
}

function UpcomingEventsSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ctx = useContext(HGContext);
  const shortDate = useShortDate();
  const year = new Date().getFullYear();
  const cid = congId(ctx);
  const thisYear = useEvents(cid, year, { enabled: !!cid }).data ?? [];
  const nextYear = useEvents(cid, year + 1, { enabled: !!cid }).data ?? [];
  const today = new Date().toISOString().slice(0, 10);
  // Non-custom event types (co, ca, rc, ...) are keyed per affected meeting date, so a single
  // logical event (e.g. a CO visit touching both the midweek and weekend meeting) can have more
  // than one row for the same week — collapse those down to one entry here.
  const seenWeeks = new Set<string>();
  const events = [...thisYear, ...nextYear]
    .filter((e) => e.date >= today)
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .filter((e) => {
      if (e.event === Events.custom) return true;
      const key = e.event + "|" + e.week;
      if (seenWeeks.has(key)) return false;
      seenWeeks.add(key);
      return true;
    })
    .slice(0, 5);
  return (
    <section className="hp-railsec">
      <h3 className="hp-railh">
        <CalendarEvent size={14} /> {t("home.rail.events")}
      </h3>
      {events.length === 0 && <div className="hp-railempty">{t("general.none")}</div>}
      <ul className="hp-events">
        {events.map((e) => {
          const custom = e.event === Events.custom;
          const title = custom ? e.custom_title || t("schedules.events.custom") : t(EventTypes[e.event]);
          return (
            <li key={e.id} className={"hp-event " + (custom ? "hp-event--custom" : "hp-event--event")}>
              <span className="hp-event__when">{shortDate(e.date, true)}</span>
              <span className="hp-event__title">{title}</span>
            </li>
          );
        })}
      </ul>
      {events.length > 0 && (
        <a
          className="hp-railmore"
          onClick={() => navigate("/scheduling/events")}
          role="link"
          tabIndex={0}
          style={{ cursor: "pointer" }}
        >
          {t("general.calendar")}
        </a>
      )}
    </section>
  );
}
